import { useEffect, useState } from "react";

import type { PurDocumentVerificationAttempt } from "react-lib/apis/PUR/purDocumentVerification";
import { PurClientConfig, purRequestOptions, readId } from "react-lib/apis/PUR/purRead";

interface VerificationSession {
  attempt: PurDocumentVerificationAttempt | null;
  uncertain: boolean;
  inFlight: boolean;
}

// Owned by the workspace, so closing a detail or changing tabs cannot forget
// an ambiguous write. Context keys remain in memory and never enter the DOM.
export const createPurVerificationStore = () => {
  const sessions = new Map<string, VerificationSession>();
  const listeners = new Set<() => void>();
  return {
    session: (client: PurClientConfig, documentId: number): VerificationSession => {
      const key = JSON.stringify([purRequestOptions(client, "").url, client.apiToken, readId(client.divisionId), documentId]);
      let session = sessions.get(key);
      if (!session) {
        session = { attempt: null, uncertain: false, inFlight: false };
        sessions.set(key, session);
      }
      return session;
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    changed: () => { listeners.forEach((listener) => listener()); },
    pending: () => [...sessions.values()].some((session) => session.inFlight || session.uncertain),
  };
};

export type PurVerificationStore = ReturnType<typeof createPurVerificationStore>;

export const usePurVerificationStore = (): PurVerificationStore => {
  const [store] = useState(createPurVerificationStore);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (store.pending()) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);
  return store;
};
